/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import _ from 'underscore';
import { showRemoveStylesToast, showUnsupportedExtVisToast } from 'util/ToastUtil';
import { isExtVis } from 'util/ExtVisUtil';
class CopyPasteHandler {
	constructor(options) {
		this.dashboardApi = options.dashboardApi;
		this.boardModel = options.boardModel;
		this.exploreContainerLayoutModel = options.exploreContainerLayoutModel;
		this.glassContext = options.glassContext;
	}

	/**
	* extract cards and widgets into an explore spec, copy the spec to clipboard
	* @param cards array of card layouts
	*/
	doCopyCards(cardLayouts) {
		const cards = cardLayouts.map(cardLayout => cardLayout.toJSON());
		return this._doCopyCards(cards);
	}
	/**
	 *
	 * @param {*} cards
	 * @returns {Promise}
	 */
	_doCopyCards(cards) {
		return Promise.all([
			this._getSpec(cards),
			this.dashboardApi.getDashboardSvc('.CopyPasteController')
		]).then(([spec, copyPasteController]) => {
			const sharedSpec = {
				type: 'EXPLORE',
				spec: JSON.stringify(spec),
				count: cards.length
			};
			return copyPasteController.doCopy(sharedSpec)
				.then(() => sharedSpec);
		});
	}

	_getNewSingleWidgetCard(card, widget) {
		return {
			id: _.uniqueId(card.id),
			type: 'exploreCard',
			items: [
				{
					id: widget.id,
					content: widget,
					type: 'widget'
				}
			],
			isHidden: card.isHidden,
			isFavourite: card.isFavourite
		};
	}
	/**
	* extract widget into explore spec, copy the spec to clipboard
	* @param {string} cardId - selected card id
	* @param {object}
	*/
	doCopyWidgets(cardId, widgets) {
		const card = this.boardModel.layout.items.find(card => card.id === cardId);
		if (widgets && widgets.length > 0) {
			const newCards = widgets.map(widget => this._getNewSingleWidgetCard(card, widget));
			return this._doCopyCards(newCards);
		}
		return this.doCopyCards([card]);
	}
	/**
	* paste to explore if data in clipboard is a spec of explore, dashboard, story and report
	* @param {insertBefore} insert before card id
	*/
	doPaste() {
		return this.dashboardApi.getDashboardSvc('.CopyPasteController')
			.then(srvc => {
				return srvc.doPaste('EXPLORE');
			})
			.then(specJson => {
				if (specJson) {
					specJson.widgets && specJson.layout && this._removeCustomVis(specJson);
					let isStylesStripped = false;
					if(specJson.hasOwnProperty('isStylesStripped')){
						if(specJson.isStylesStripped === true){
							isStylesStripped = true;
						}
						delete specJson.isStylesStripped;
					}
					for (const widgetId in specJson.widgets) {
						if (specJson.widgets.hasOwnProperty(widgetId)) {
							specJson.widgets[widgetId].titleMode = 'smartTitle';
						}
					}
					if(isStylesStripped){
						//Show toast message if any styles have been stripped off
						showRemoveStylesToast(this.glassContext);
					}
					return specJson && this._apply(specJson);
				}
			});
	}

	_removeCustomVis(spec) {
		const customVisIds = Object.keys(spec.widgets).filter( id => {
			return isExtVis(spec.widgets[id].visId);
		});
		// Remove custom vis from spec
		if(customVisIds.length > 0 ) {
			customVisIds.forEach(id => {
				delete spec.widgets[id];
			});
			const customVisCardLayoutIds = this._getCustomVisCardLayoutIds(spec.layout.items, customVisIds);
			spec.layout.items = this._filterOutCustomVisCardLayout(spec.layout.items, customVisCardLayoutIds);
			showUnsupportedExtVisToast(this.glassContext);
		}
	}

	_getCustomVisCardLayoutIds(layoutItems = [], customVisIds) {
		const ids = [];
		layoutItems.forEach( (layoutItem) => {
			layoutItem.items.forEach( item => {
				if(customVisIds.indexOf(item.id) !== -1){
					ids.push(layoutItem.id);
				}
			});
		});
		return ids;
	}

	_filterOutCustomVisCardLayout(layoutItems = [], customVisCardLayoutIds) {
		return layoutItems.filter( item => {
			return customVisCardLayoutIds.indexOf(item.id) === -1;
		});
	}

	_getSpec(cards) {
		const widgets = {};
		cards.forEach(card => card.items.forEach(item => {
			if (item.type === 'widget') {
				const sourceWidgetJSON=this.boardModel.findWidgetById(item.id);
				if (sourceWidgetJSON) {
					//make sure we're working with cloned specs (widget filters might get modified)
					widgets[item.id] = JSON.parse(JSON.stringify(sourceWidgetJSON));
				}
			}
		}));

		//datasource
		const allSources = this.boardModel.dataSources.sources;
		const sourceMap = {};
		const promises = [];
		Object.values(widgets).forEach(widget => {
			if (widget.data && widget.data.dataViews) {
				widget.data.dataViews.forEach(dataView => {
					const source = allSources.find(source => source.id === dataView.modelRef);
					if (source && !sourceMap[source.id]) {
						sourceMap[source.id] = source;
					}
				});
			}

			/** convert dock filters to local filters and save in the spec */
			promises.push(
				this.dashboardApi.getCanvasWhenReady()
					.then(canvas => canvas.getContent(widget.id))
					.then((content) => {
						const filters = this._getAllFiltersAsLocalFilters(content);
						if(filters){
							widget.localFilters = widget.localFilters || [];
							Object.assign(widget.localFilters, filters);
						}
					})
			);
		});
		const sources = Object.values(sourceMap);

		return Promise.all(promises).then(() => {
			//return explore spec
			return {
				name: '',
				layout: {
					id: '',
					items: cards,
					type: 'exploreContainer'
				},
				dataSources: {
					sources: sources ? sources : []
				},
				widgets: widgets
			};
		});
	}

	_getAllFiltersAsLocalFilters(content){
		let localFilters = [];
		let globalFilters = [];
		const visualization = content && content.getFeature('Visualization');
		if (visualization){
			//Note: could have gotten localFilters directly from the spec but this is the more generic way.
			localFilters = visualization.getLocalFilters().getFilterList();

			//Add the filterDock filters for this card to the filters for the widget.
			const globalFiltersFeature = this.dashboardApi.getFeature('GlobalFilters');
			if (globalFiltersFeature){
				const sourceId = visualization.getDataSource() && visualization.getDataSource().getId();
				const widgetApi = content.getFeature('WidgetAPI.deprecated');
				const scope = widgetApi.getScope();
				globalFilters = globalFiltersFeature.getFilters(sourceId, scope).getFilterList();
			}
		}
		return [...localFilters, ...globalFilters];
	}

	_apply(exploreSpec) {
		//merge into explore
		const payloadData = {
			undoRedoTransactionId: _.uniqueId('paste')
		};
		const options = {
			insertBefore: null,
			parentId: this.exploreContainerLayoutModel.id,
			model: exploreSpec
		};
		return this.exploreContainerLayoutModel.addFragment(options, 'CopyPasteHandler', payloadData);
	}
}

export default CopyPasteHandler;



// WEBPACK FOOTER //
// ./src/js/util/CopyPasteHandler.js